import { useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import Button from '../../juristec-ui/core/Button';
import ActionsGroup from '../../juristec-ui/core/ActionsGroup';
import {
  defaultReportGridSettings,
  ReportGridSettingsForm,
} from './ReportGridSettingsForm';
import { buildReportGridModel } from './core/model';

export default function ReportGridEditor({
  initialValue,
  createConfiguration,
  renderDataControls,
  renderFilterControls,
  canApply,
  onGenerate,
  onSave,
  onApply,
  onCancel,
}) {
  const theme = useTheme();
  const [configuration, setConfiguration] = useState(
    () => initialValue?.configuration ?? createConfiguration,
  );

  const [settings, setSettings] = useState(
    () => initialValue?.settings ?? {
      ...defaultReportGridSettings,
      appearance: {
        ...defaultReportGridSettings.appearance,
        accent: theme.primary,
        background: theme.tableBackground,
        text: theme.color,
        header: theme.controllersLabel,
        border: theme.containerLine,
        stripeBackground: theme.container,
        stripeText: theme.color,
        rowSubtotalBackground: theme.controllersLabel,
        rowSubtotalText: theme.color,
        columnSubtotalBackground: theme.container,
        columnSubtotalText: theme.color,
        total: theme.controllersLabel,
        totalText: theme.color,
        negativeText: theme.error,
        highlightBackground: theme.warning,
        highlightText: theme.dark,
        cellHighlightBackground: theme.warning,
        cellHighlightText: theme.dark,
        cellHighlightBorder: theme.primary,
      },
      title: {
        ...defaultReportGridSettings.title,
        color: theme.color,
        background: theme.tableBackground,
      },
    },
  );

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const request = useRef(null);

  useEffect(() => () => {
    request.current?.abort();
  }, []);

  const cancel = () => {
    request.current?.abort();
    onCancel();
  };

  const apply = async () => {
    if (busy || canApply?.(configuration) === false) return;

    const controller = new AbortController();
    request.current = controller;

    setBusy(true);
    setError('');

    try {
      const dataChanged = (
        !initialValue
        || !initialValue.dataset
        || JSON.stringify(configuration)
          !== JSON.stringify(initialValue.configuration)
      );

      const dataset = dataChanged
        ? await onGenerate(configuration, controller.signal)
        : initialValue.dataset;

      if (controller.signal.aborted) return;

      // Valida o dataset antes de salvar ou exibir.
      buildReportGridModel(dataset);

      const definition = {
        version: 1,
        configuration,
        settings,
        dataset,
      };

      await onSave?.(definition, controller.signal);

      if (controller.signal.aborted) return;

      onApply(definition);
    } catch (failure) {
      if (!controller.signal.aborted) {
        setError(
          failure.message || 'Não foi possível gerar o relatório.',
        );
      }
    } finally {
      if (!controller.signal.aborted) setBusy(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      overflow: 'auto',
      padding: '0 5px 5px 0',
    }}
    >
      <fieldset disabled={busy} style={{ border: 0, padding: 0 }}>
        <ReportGridSettingsForm
          value={settings}
          onChange={setSettings}
          disabled={busy}
          dataControls={renderDataControls(
            configuration,
            setConfiguration,
          )}
          filterControls={renderFilterControls?.(configuration, setConfiguration, busy)}
        />
      </fieldset>

      {error && <p role="alert">{error}</p>}

      <ActionsGroup>
        <Button
          type="button"
          style={{ margin: '5px' }}
          onClick={cancel}
          variant="outlined"
          size="large"
        >
          Cancelar
        </Button>
        <Button
          type="button"
          style={{ margin: '5px' }}
          onClick={apply}
          size="large"
          disabled={busy || canApply?.(configuration) === false}
        >
          {(busy && 'Aplicando…')
          || (initialValue && 'Aplicar')
          || 'Gerar relatório'}
        </Button>
      </ActionsGroup>
    </div>
  );
}
